import { ANALYTICS_CONFIG } from '../config/analyticsConfig';

/**
 * Check if analytics should be enabled
 */
const isAnalyticsEnabled = () => {
  const isDev = process.env.NODE_ENV === 'development';

  if (isDev && !ANALYTICS_CONFIG.ENABLE_IN_DEVELOPMENT) {
    return false;
  }

  return ANALYTICS_CONFIG.GOOGLE_ANALYTICS_ID || ANALYTICS_CONFIG.META_PIXEL_ID;
};

/**
 * Initialize Google Analytics
 */
export const initGoogleAnalytics = () => {
  if (!ANALYTICS_CONFIG.GOOGLE_ANALYTICS_ID) {
    console.log('[Analytics] Google Analytics ID not configured');
    return;
  }

  // Load Google Analytics script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${ANALYTICS_CONFIG.GOOGLE_ANALYTICS_ID}`;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;

  gtag('js', new Date());
  gtag('config', ANALYTICS_CONFIG.GOOGLE_ANALYTICS_ID, {
    send_page_view: true,
  });

  console.log('[Analytics] Google Analytics initialized');
};

/**
 * Initialize Meta Pixel
 */
export const initMetaPixel = () => {
  if (!ANALYTICS_CONFIG.META_PIXEL_ID) {
    console.log('[Analytics] Meta Pixel ID not configured');
    return;
  }

  try {
    // Load Meta Pixel script
    !(function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;

      // Handle script load error (blocked by ad blocker)
      t.onerror = function() {
        console.log('[Analytics] Meta Pixel blocked by ad blocker or privacy extension (this is normal in development)');
      };

      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

    window.fbq('init', ANALYTICS_CONFIG.META_PIXEL_ID);
    window.fbq('track', 'PageView');

    // Add noscript fallback image for users with JavaScript disabled
    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.src = `https://www.facebook.com/tr?id=${ANALYTICS_CONFIG.META_PIXEL_ID}&ev=PageView&noscript=1`;
    noscript.appendChild(img);
    document.body.appendChild(noscript);

    console.log('[Analytics] Meta Pixel initialized (may be blocked by ad blocker in development)');
  } catch (error) {
    console.log('[Analytics] Meta Pixel initialization failed (likely blocked by ad blocker):', error.message);
  }
};

/**
 * Initialize all analytics
 */
export const initAnalytics = () => {
  if (!isAnalyticsEnabled()) {
    console.log('[Analytics] Analytics disabled');
    return;
  }

  initGoogleAnalytics();
  initMetaPixel();
};

/**
 * Track Google Analytics event
 */
const trackGAEvent = (eventName, eventParams = {}) => {
  if (window.gtag && ANALYTICS_CONFIG.GOOGLE_ANALYTICS_ID) {
    window.gtag('event', eventName, eventParams);
    console.log(`[GA] Event tracked: ${eventName}`, eventParams);
  }
};

/**
 * Track Meta Pixel event
 */
const trackMetaEvent = (eventName, eventParams = {}) => {
  if (window.fbq && ANALYTICS_CONFIG.META_PIXEL_ID) {
    try {
      window.fbq('track', eventName, eventParams);
      console.log(`[Meta Pixel] Event tracked: ${eventName}`, eventParams);
    } catch (error) {
      // Silently handle if Meta Pixel is blocked
      console.log(`[Meta Pixel] Event not tracked (blocked): ${eventName}`);
    }
  }
};

/**
 * Track survey submission (submit button click)
 */
export const trackSurveySubmit = () => {
  if (!isAnalyticsEnabled()) return;

  // Google Analytics
  trackGAEvent('survey_submit', {
    event_category: 'Survey',
    event_label: 'Survey Form Submitted',
  });

  // Meta Pixel - using 'CompleteRegistration' standard event
  trackMetaEvent('CompleteRegistration', {
    content_name: 'Beer Survey',
    status: 'completed',
  });
};

/**
 * Track success page link clicks
 */
export const trackSuccessLinkClick = (linkType, linkUrl, couponBrand = '') => {
  if (!isAnalyticsEnabled()) return;

  // Google Analytics
  trackGAEvent('success_link_click', {
    event_category: 'Success Page',
    event_label: linkType,
    link_url: linkUrl,
    coupon_brand: couponBrand,
  });

  // Meta Pixel
  trackMetaEvent('ViewContent', {
    content_type: 'success_link',
    content_name: linkType,
    content_category: couponBrand,
  });
};

/**
 * Track footer banner clicks
 */
export const trackFooterBannerClick = (bannerName, bannerUrl) => {
  if (!isAnalyticsEnabled()) return;

  // Google Analytics
  trackGAEvent('footer_banner_click', {
    event_category: 'Footer',
    event_label: bannerName,
    link_url: bannerUrl,
  });

  // Meta Pixel
  trackMetaEvent('ViewContent', {
    content_type: 'footer_banner',
    content_name: bannerName,
    content_category: 'Footer Sponsors',
  });
};

/**
 * Track page view (for custom page tracking)
 */
export const trackPageView = (pageName) => {
  if (!isAnalyticsEnabled()) return;

  // Google Analytics
  trackGAEvent('page_view', {
    page_title: pageName,
  });

  // Meta Pixel
  trackMetaEvent('PageView', {
    content_name: pageName,
  });
};
